/**
 * @file 登录页
 * @description 思犇优学账号密码登录入口；当前使用固定演示凭据校验。
 *
 * @whenToUse 未登录访问应用时的登录入口（路由 `/login`）。
 * @whenNotToUse 已登录后的业务页；不要在此堆叠后台壳层（侧栏/顶栏）。
 */

import { useState, type SubmitEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, User } from 'lucide-react'
import iconUrl from '@/assets/icon.png'
import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { Input } from '@/components/ui/input.tsx'
import { toast } from '@/components/ui/toast.tsx'
import {
  clearRememberedUsername,
  getRememberedUsername,
  setAuthToken,
  setRememberedUsername,
} from '@/lib/auth.ts'
import { loginApi } from '@/api/auth.ts'

/**
 * 登录页：左上角品牌 Logo + 居中登录表单。
 */
export function LoginPage() {
  return (
    <div className="relative flex min-h-full flex-col bg-canvas text-foreground">
      <div className="absolute top-6 left-6 inline-flex items-center">
        <img
          src={iconUrl}
          alt="思犇优学"
          className="size-12 rounded-lg object-cover"
        />
      </div>

      <div className="flex flex-1 items-center justify-center px-6 py-16">
        <LoginForm />
      </div>
    </div>
  )
}

/**
 * 账号密码登录表单；支持记住账号，校验通过后跳转 `/`。
 */
function LoginForm() {
  const navigate = useNavigate()
  const rememberedUsername = getRememberedUsername()
  const [username, setUsername] = useState(rememberedUsername ?? '')
  const [password, setPassword] = useState('')
  const [rememberUsername, setRememberUsername] = useState(
    Boolean(rememberedUsername),
  )
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  /**
   * 提交登录：调用登录接口，按需记住账号，成功跳转 `/`。
   * @param event 表单提交事件
   */
  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    setError('')

    const nextUsername = username.trim()

    if (!nextUsername || !password) {
      setError('请输入账号和密码')
      return
    }

    setSubmitting(true)

    try {
      const { access_token } = await loginApi({
        username: nextUsername,
        password,
        rememberMe: rememberUsername,
        tenantId: '000000',
      })
      setAuthToken(access_token)
      if (rememberUsername) {
        setRememberedUsername(nextUsername)
      } else {
        clearRememberedUsername()
      }
      toast.success('登录成功')
      navigate('/', { replace: true })
    } catch (error) {
      setError(error instanceof Error ? error.message : '登录失败')
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-sm space-y-6 rounded-2xl border border-border bg-surface p-6"
      noValidate
    >
      <div className="space-y-1.5 text-center">
        <h1 className="text-xl font-semibold tracking-tight">
          欢迎登录思犇优学
        </h1>
        <p className="text-sm text-muted">请使用账号密码登录</p>
      </div>

      <div className="space-y-4">
        <FormField label="账号" htmlFor="login-username" required>
          <Input
            id="login-username"
            name="username"
            autoComplete="username"
            placeholder="请输入账号"
            value={username}
            wrapperClassName="h-9.5!"
            invalid={Boolean(error)}
            prefix={<User aria-hidden />}
            onChange={(event) => {
              setUsername(event.target.value)
              if (error) setError('')
            }}
          />
        </FormField>

        <FormField label="密码" htmlFor="login-password" required>
          <Input
            id="login-password"
            name="password"
            autoComplete="current-password"
            placeholder="请输入密码"
            type="password"
            showPasswordToggle
            value={password}
            wrapperClassName="h-9.5!"
            invalid={Boolean(error)}
            prefix={<Lock aria-hidden />}
            onChange={(event) => {
              setPassword(event.target.value)
              if (error) setError('')
            }}
          />
        </FormField>

        <Checkbox
          id="login-remember-username"
          label="记住账号"
          checked={rememberUsername}
          onChange={(event) => setRememberUsername(event.target.checked)}
        />

        {error ? <p className="text-xs text-danger">{error}</p> : null}
      </div>

      <Button type="submit" className="h-9.5! w-full" loading={submitting}>
        登录
      </Button>
    </form>
  )
}
